// Re-authentication before a sensitive action (Domain/Reauthentication.cs). A form marked data-reauth first asks the
// server whether this browser's proof is still fresh; if not, a dialog takes the password, or a code by SMS, and the
// form then goes on. The server checks again on the action itself, so this only spares the user a refusal.

type Answer = { ok: boolean; error?: string; message?: string; signedOut?: boolean };

let resolveOpen: ((ok: boolean) => void) | null = null;

function el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function token(): string {
  return el<HTMLFormElement>("reauth-form")?.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]")?.value ?? "";
}

async function fresh(): Promise<{ fresh: boolean; canSms: boolean }> {
  try {
    const res = await fetch("/Reauth/Status", { headers: { "X-Requested-With": "XMLHttpRequest" }, cache: "no-store" });
    if (res.ok) return (await res.json()) as { fresh: boolean; canSms: boolean };
  } catch { /* the dialog decides */ }
  return { fresh: false, canSms: false };
}

async function post(path: string, body: Record<string, string>): Promise<Answer> {
  const form = new URLSearchParams(body);
  form.set("__RequestVerificationToken", token());
  try {
    const res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", "X-Requested-With": "XMLHttpRequest" },
      body: form,
    });
    if (res.ok) return (await res.json()) as Answer;
  } catch { /* falls through */ }
  return { ok: false, error: "אין חיבור לשרת. נסו שוב" };
}

/** Resolves true once the proof is fresh, asking for it when needed; false when the user cancels. */
export async function ensureReauth(): Promise<boolean> {
  const dialog = el("reauth");
  if (!dialog) return true;   // signed out pages have nothing to confirm
  const state = await fresh();
  if (state.fresh) return true;
  if (resolveOpen) resolveOpen(false);
  return new Promise<boolean>((resolve) => {
    resolveOpen = resolve;
    setMode("password");
    el<HTMLElement>("reauth-sms")!.hidden = !state.canSms;
    say("", "");
    dialog.hidden = false;
    window.setTimeout(() => el<HTMLInputElement>("reauth-password")?.focus(), 30);
  });
}

let mode: "password" | "code" = "password";

function setMode(m: "password" | "code"): void {
  mode = m;
  document.querySelectorAll<HTMLElement>("#reauth [data-mode]").forEach((x) => { x.hidden = x.dataset.mode !== m; });
  const pw = el<HTMLInputElement>("reauth-password");
  const code = el<HTMLInputElement>("reauth-code");
  if (pw) pw.value = "";
  if (code) code.value = "";
}

function say(error: string, note: string): void {
  const e = el<HTMLElement>("reauth-error");
  const n = el<HTMLElement>("reauth-note");
  if (e) { e.textContent = error; e.hidden = !error; }
  if (n) { n.textContent = note; n.hidden = !note; }
}

function close(ok: boolean): void {
  const dialog = el("reauth");
  if (dialog) dialog.hidden = true;
  const r = resolveOpen;
  resolveOpen = null;
  setMode("password");
  r?.(ok);
}

/** Submits the form as if its button was pressed, past this check: the submitter's name and value go along. */
function proceed(form: HTMLFormElement, submitter: HTMLElement | null): void {
  form.dataset.reauthPassed = "1";
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
    if (submitter.name) {
      const carry = document.createElement("input");
      carry.type = "hidden";
      carry.name = submitter.name;
      carry.value = submitter.value;
      form.appendChild(carry);
    }
  }
  HTMLFormElement.prototype.submit.call(form);
}

export function initReauth(): void {
  const dialog = el("reauth");
  const form = el<HTMLFormElement>("reauth-form");
  if (!dialog || !form) return;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
    if (button) button.disabled = true;
    const answer = mode === "password"
      ? await post("/Reauth/Password", { password: el<HTMLInputElement>("reauth-password")?.value ?? "" })
      : await post("/Reauth/Code", { code: el<HTMLInputElement>("reauth-code")?.value ?? "" });
    if (button) button.disabled = false;
    if (answer.ok) { close(true); return; }
    if (answer.signedOut) { window.location.href = "/Login"; return; }
    say(answer.error ?? "האימות נכשל", "");
  });

  dialog.querySelector<HTMLElement>("[data-reauth-cancel]")?.addEventListener("click", () => close(false));
  dialog.addEventListener("keydown", (e) => { if (e.key === "Escape") close(false); });
  el<HTMLElement>("reauth-sms")?.addEventListener("click", async () => {
    const answer = await post("/Reauth/RequestCode", {});
    if (!answer.ok) { say(answer.error ?? "לא ניתן לשלוח קוד", ""); return; }
    setMode("code");
    say("", answer.message ?? "");
    el<HTMLInputElement>("reauth-code")?.focus();
  });

  // Any form marked data-reauth. A handler that already stopped the form (a confirm or a prompt that was declined)
  // is left alone; one that let it through has run already, so the form is sent on as it stands.
  document.addEventListener("submit", async (e) => {
    const target = e.target;
    if (!(target instanceof HTMLFormElement) || !target.hasAttribute("data-reauth") || e.defaultPrevented) return;
    if (target.dataset.reauthPassed === "1") return;
    e.preventDefault();
    const submitter = (e as SubmitEvent).submitter ?? null;
    if (await ensureReauth()) proceed(target, submitter);
  });
}
